import { gql, useQuery } from '@apollo/client';
import { VStack, FormControl, FormLabel, RadioGroup, Radio, Text } from '@chakra-ui/react';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import {
  EGroupFindPermission,
  EGroupSelfJoinPermission,
  EGroupViewContentPermission,
} from '@/graphql-types/globalTypes';
import { useValidFindPermissionOptions } from '@/hooks/useValidFindPermissionOptions';

import { GroupSettingsForm_Tenant } from './graphql-types/GroupSettingsForm_Tenant';

import { CanFindConfig } from './components/CanFindConfig';
import { CanJoinConfig } from './components/CanJoinConfig/CanJoinConfig';
import { CanViewContentConfig } from './components/CanViewContentConfig';
import { PaidSponsorInfo } from './components/PaidSponsorInfo';

const TENANT_INFO = gql`
  query GroupSettingsForm_Tenant {
    tenant {
      result {
        _id
        hasActiveSponsorContracts
      }
    }
  }
`;

const GET_CC_STATUS = gql`
  query SettingsForm_GetCCStatus {
    emailMarketingIntegrationConstantContactGetStatus {
      status
    }
  }
`;

const getShowPaidSponsorAdminAlert = (options: {
  hasPayingSponsors: boolean;
  selectedFindConfig: EGroupFindPermission;
  selectedJoinConfig?: EGroupSelfJoinPermission;
  selectedCanViewContentConfig?: EGroupViewContentPermission;
}): boolean => {
  const {
    hasPayingSponsors,
    selectedFindConfig,
    selectedJoinConfig,
    selectedCanViewContentConfig,
  } = options;
  if (!hasPayingSponsors) {
    return false;
  }
  return (
    ([EGroupFindPermission.PUBLIC, EGroupFindPermission.LOGGED_IN].includes(
      selectedFindConfig
    ) &&
      (selectedCanViewContentConfig ===
        EGroupViewContentPermission.SAME_AS_FIND ||
        selectedJoinConfig === EGroupSelfJoinPermission.LOGGED_IN)) ||
    (selectedFindConfig === EGroupFindPermission.MEMBER_STATUS_USERS &&
      selectedJoinConfig === EGroupSelfJoinPermission.MEMBER_STATUS_USERS &&
      selectedCanViewContentConfig === EGroupViewContentPermission.SAME_AS_FIND)
  );
};

type IGroupPrivacyFormData = {
  canFindConfig: EGroupFindPermission;
  canJoinConfig?: EGroupSelfJoinPermission;
  canViewContentConfig?: EGroupViewContentPermission;
  selectedPeopleAttributes?: string[];
  configSelection?: string[];
  peopleAttributes?: {
    defaultAttributes: Array<{
      _id: string;
      values: string[];
      name: string;
      __typename: string;
    }>;
    customAttributes: Array<{
      schemaId: string;
      values: string[];
      name: string;
      __typename: string;
    }>;
  };
  syncGroupWithCC?: boolean;
};

export const SettingsForm: React.FC<{
  isEditing: boolean;
  parentGroupId?: string;
  onRequestJoinChange?: (
    newValue: EGroupSelfJoinPermission
  ) => Promise<boolean>;
  onRequestFindChange?: (
    newValue: EGroupFindPermission
  ) => Promise<boolean>;
  isJoinChangeLoading?: boolean;
  isFindChangeLoading?: boolean;
}> = ({
  isEditing,
  parentGroupId,
  onRequestJoinChange,
  onRequestFindChange,
  isJoinChangeLoading,
  isFindChangeLoading,
}) => {
  const { data, loading } = useQuery<GroupSettingsForm_Tenant>(TENANT_INFO);
  const { data: ccData } = useQuery(GET_CC_STATUS);

  const { watch, formState } = useFormContext<IGroupPrivacyFormData>();

  const findConfigValue = watch('canFindConfig');
  const joinConfigValue = watch('canJoinConfig');
  const viewContentConfigValue = watch('canViewContentConfig');
  const selectedPeopleAttributes = watch('selectedPeopleAttributes');
  const syncGroupWithCCValue = watch('syncGroupWithCC');

  const { validFindPermissionOptions } = useValidFindPermissionOptions({
    parentGroupId,
  });

  const showCCSync = React.useMemo(() => {
    return !parentGroupId && 
      ccData?.emailMarketingIntegrationConstantContactGetStatus?.status === 'INITIALIZED';
  }, [parentGroupId, ccData]);

  if (!validFindPermissionOptions) {
    return null;
  }

  return (
    <VStack spacing={isEditing ? '4' : '8'} align="stretch" w="full">
      <CanFindConfig
        isEditing={isEditing}
        availableFindPermissions={Array.from(validFindPermissionOptions.keys())}
        onRequestFindChange={onRequestFindChange}
        isFindChangeLoading={isFindChangeLoading}
      />
      <CanJoinConfig
        isEditing={isEditing}
        availablePermissions={validFindPermissionOptions}
        selectedPeopleAttributes={selectedPeopleAttributes} // Pass this prop
        formState={formState} // Pass formState for validation errors
        onRequestJoinChange={onRequestJoinChange}
        isJoinChangeLoading={isJoinChangeLoading}
      />
      <CanViewContentConfig
        isEditing={isEditing}
        availablePermissions={validFindPermissionOptions}
      />
      {showCCSync && (
        <FormControl as="fieldset">
          <FormLabel as="legend">Enable Sync with Constant Contact</FormLabel>
          {isEditing ? (
            <Controller
              name="syncGroupWithCC"
              render={({ field: { onChange, value, ref } }) => (
                <RadioGroup value={value ? 'true' : 'false'} onChange={(val) => onChange(val === 'true')} ref={ref}>
                  <VStack spacing="2" width="full" alignItems="start">
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </VStack>
                </RadioGroup>
              )}
            />
          ) : (
            <Text>{syncGroupWithCCValue ? 'Yes' : 'No'}</Text>
          )}
        </FormControl>
      )}
      {getShowPaidSponsorAdminAlert({
        hasPayingSponsors:
          !loading && !!data?.tenant.result?.hasActiveSponsorContracts,
        selectedFindConfig: findConfigValue,
        selectedJoinConfig: joinConfigValue,
        selectedCanViewContentConfig: viewContentConfigValue,
      }) && <PaidSponsorInfo />}
    </VStack>
  );
};
